import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Layout
import MainLayout from './components/layout/MainLayout';

// Auth Pages
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import RegisterSuccess from './components/auth/RegisterSuccess';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';

// Dashboard
import Dashboard from './components/dashboard/Dashboard';

// Clubs Management
import ClubsPage from './components/clubs/ClubsPage';

// User Management
import UserManagementPage from './components/users/UserManagementPage';
import UserDetailsPage from './components/users/UserDetailsPage';

// Member Management  
import MemberManagementPage from './components/members/MemberManagementPage';
import MemberProfileDetailsPage from './components/members/MemberProfileDetailsPage';
import ChangeMembershipPage from './components/members/ChangeMembershipPage';

// Membership Management
import MembershipTierPage from './components/membership/MembershipTierPage';
import MembershipApplicationsPage from './components/membership/MembershipApplicationsPage';
import MembershipApplicationReviewPage from './components/membership/MembershipApplicationReviewPage';
import MembersPage from './components/members/MembersPage';
import CreateMemberAccountPage from './components/members/CreateMemberAccountPage';

// Class Management
import ClassManagementPage from './components/classes/ClassManagementPage';
import ViewClassPage from './components/classes/ViewClassPage';
import ViewClassInstancePage from './components/classes/ViewClassInstancePage';
import EditClassPage from './components/classes/EditClassPage';

// Coaching Management
import CoachingDashboardPage from './components/coaching/CoachingDashboardPage';
import CoachingSessionManagementPage from './components/coaching/CoachingSessionManagementPage';
import CoachingProgramManagementPage from './components/coaching/CoachingProgramManagementPage';
import ViewCoachingProgramPage from './components/coaching/ViewCoachingProgramPage';
import ViewCoachingProgramInstancePage from './components/coaching/ViewCoachingProgramInstancePage';
import ViewCoachingSessionPage from './components/coaching/ViewCoachingSessionPage';
import EditCoachingSessionPage from './components/coaching/EditCoachingSessionPage';
import EditCoachingProgramPage from './components/coaching/EditCoachingProgramPage';

// Personal Training Management
import PersonalTrainingsPage from './components/personalTraining/PersonalTrainingsPage';

// GroupPlay Management
import GroupPlayManagementPage from './components/groupPlay/GroupPlayManagementPage';
import CreateEditGroupPlayPage from './components/groupPlay/CreateEditGroupPlayPage';
import ViewGroupPlayPage from './components/groupPlay/ViewGroupPlayPage';
import ViewGroupPlayInstancePage from './components/groupPlay/ViewGroupPlayInstancePage';

// Amenity Management
import AmenityManagementPage from './components/amenities/AmenityManagementPage';
import AmenitySchedulePage from './components/amenities/AmenitySchedulePage';
import CourtsCalendar from './components/amenities/CourtsCalendar';
import AmenityBookingDetailPage from './components/amenities/AmenityBookingDetailPage';
import AmenityBookingPage from './pages/members/AmenityBookingPage';

// Equipment Management
import EquipmentManagementPage from './components/equipment/EquipmentManagementPage';

// Common Components
import UniversalCalendar from './components/common/UniversalCalendar';

// Member Components
import MemberClassesPage from './components/member/MemberClassesPage';
import MemberGroupPlaysPage from './components/member/MemberGroupPlaysPage';
import ComingSoonPage from './components/member/ComingSoonPage';
import ClassBookingPage from './pages/members/ClassBookingPage';
import GroupPlayBookingPage from './pages/members/GroupPlayBookingPage';
import MemberAmenityBooking from './components/member/MemberAmenityBooking';
import CoachingProgramsPage from './pages/members/CoachingProgramsPage';

// Transactions
import TransactionsPage from './components/transactions/TransactionsPage';

// Placeholder components (to be implemented later)
const NotFound = () => <div>Page Not Found</div>;

// Import actual components
import SettingsPage from './components/settings/SettingsPage';
import DiscountPage from './components/discounts/DiscountPage';
import GroupsPage from './components/groups/GroupsPage';
import OneTimeFeesPage from './components/fees/OneTimeFeesPage';
import ProfilePage from './components/profile/ProfilePage';
import MemberProfilePage from './pages/members/MemberProfilePage';
import SelectMemberProfilePage from './pages/members/SelectMemberProfilePage';
import BillingPage from './pages/members/BillingPage';
import DocumentReviewPage from './pages/members/DocumentReviewPage';
import AddOnManagementPage from './components/addOns/AddOnManagementPage';

// Events
import EventsPage from './components/events/EventsPage';
import EventManagementPage from './components/events/EventManagementPage';
import EventDetailsPage from './components/events/EventDetailsPage';

// Reports
import { ManagementReport } from './components/reports/ManagementReport';
import { CoachingReport } from './components/reports/CoachingReport';
import ArrearsReport from './components/reports/ArrearsReport';
import ReportsPage from './components/reports/ReportsPage';

// Audit Logs
import AuditLogsPage from './components/auditLogs/AuditLogsPage';

// Guest Pass Management
import GuestPassManagement from './pages/guestPass/GuestPassManagement';

// Kiosk View
import KioskView from './pages/kiosk/KioskView';

// Transaction Processor (Admin)
import TransactionProcessor from './pages/admin/TransactionProcessor';
import SystemNotificationsPage from './pages/admin/SystemNotificationsPage';
import { FinancialReport } from './components/reports/FinancialReport';
import { AccountingReport } from './components/reports/AccountingReport';

// Support Page
import SupportPage from './pages/support/SupportPage';

// Root redirect component - redirects to login if not authenticated, dashboard if authenticated
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.JSX.Element }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Super admin protected route wrapper
const SuperAdminProtectedRoute = ({ children }: { children: React.JSX.Element }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Create a client for React Query
const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-success" element={<RegisterSuccess />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Routes with Layout */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<RootRedirect />} />
              <Route path="dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="members" element={
                <ProtectedRoute>
                  <MembersPage />
                </ProtectedRoute>
              } />
              <Route path="amenities" element={
                <ProtectedRoute>
                  <AmenityManagementPage />
                </ProtectedRoute>
              } />
              <Route path="equipment" element={
                <ProtectedRoute>
                  <EquipmentManagementPage />
                </ProtectedRoute>
              } />
              <Route path="amenities/book" element={
                <ProtectedRoute>
                  <AmenityBookingPage />
                </ProtectedRoute>
              } />
              <Route path="amenities/:amenityId/schedule" element={
                <ProtectedRoute>
                  <AmenitySchedulePage />
                </ProtectedRoute>
              } />
              <Route path="amenities/:amenityId/bookings/:bookingId" element={
                <ProtectedRoute>
                  <AmenityBookingDetailPage />
                </ProtectedRoute>
              } />
              <Route path="calendar" element={
                <ProtectedRoute>
                  <UniversalCalendar />
                </ProtectedRoute>
              } />
              <Route path="courts-calendar" element={
                <ProtectedRoute>
                  <CourtsCalendar />
                </ProtectedRoute>
              } />
              <Route path="classes" element={
                <ProtectedRoute>
                  <MemberClassesPage />
                </ProtectedRoute>
              } />
              <Route path="coming-soon" element={
                <ProtectedRoute>
                  <ComingSoonPage />
                </ProtectedRoute>
              } />
              <Route path="member/class-booking/:instanceId" element={
                <ProtectedRoute>
                  <ClassBookingPage />
                </ProtectedRoute>
              } />
              <Route path="member/group-play" element={
                <ProtectedRoute>
                  <MemberGroupPlaysPage />
                </ProtectedRoute>
              } />
              <Route path="member/group-play-booking/:groupPlayId/:instanceId" element={
                <ProtectedRoute>
                  <GroupPlayBookingPage />
                </ProtectedRoute>
              } />
              <Route path="member/group-play/:groupPlayId/instance/:instanceId" element={
                <ProtectedRoute>
                  <GroupPlayBookingPage />
                </ProtectedRoute>
              } />
              <Route path="member/amenity-booking/:bookingId" element={
                <ProtectedRoute>
                  <MemberAmenityBooking />
                </ProtectedRoute>
              } />
              <Route path="member/coaching-programs" element={
                <ProtectedRoute>
                  <CoachingProgramsPage />
                </ProtectedRoute>
              } />
              <Route path="class-management" element={
                <ProtectedRoute>
                  <ClassManagementPage />
                </ProtectedRoute>
              } />
              <Route path="classes/:classId" element={
                <ProtectedRoute>
                  <ViewClassPage />
                </ProtectedRoute>
              } />
              <Route path="class-instances/:instanceId" element={
                <ProtectedRoute>
                  <ViewClassInstancePage />
                </ProtectedRoute>
              } />
              <Route path="classes/edit/:classId" element={
                <ProtectedRoute>
                  <EditClassPage />
                </ProtectedRoute>
              } />
              <Route path="coaching" element={
                <ProtectedRoute>
                  <CoachingDashboardPage />
                </ProtectedRoute>
              } />
              <Route path="coaching/sessions" element={
                <ProtectedRoute>
                  <CoachingSessionManagementPage />
                </ProtectedRoute>
              } />
              <Route path="coaching/programs" element={
                <ProtectedRoute>
                  <CoachingProgramManagementPage />
                </ProtectedRoute>
              } />
              <Route path="coaching/programs/:programId" element={
                <ProtectedRoute>
                  <ViewCoachingProgramPage />
                </ProtectedRoute>
              } />
              <Route path="coaching/program-instances/:instanceId" element={
                <ProtectedRoute>
                  <ViewCoachingProgramInstancePage />
                </ProtectedRoute>
              } />
              <Route path="coaching/sessions/:sessionId" element={
                <ProtectedRoute>
                  <ViewCoachingSessionPage />
                </ProtectedRoute>
              } />
              <Route path="coaching/sessions/edit/:sessionId" element={
                <ProtectedRoute>
                  <EditCoachingSessionPage />
                </ProtectedRoute>
              } />
              <Route path="coaching/programs/edit/:programId" element={
                <ProtectedRoute>
                  <EditCoachingProgramPage />
                </ProtectedRoute>
              } />
              <Route path="group-plays" element={
                <ProtectedRoute>
                  <GroupPlayManagementPage />
                </ProtectedRoute>
              } />
              <Route path="group-plays/create" element={
                <ProtectedRoute>
                  <CreateEditGroupPlayPage />
                </ProtectedRoute>
              } />
              <Route path="group-plays/edit/:groupPlayId" element={
                <ProtectedRoute>
                  <CreateEditGroupPlayPage />
                </ProtectedRoute>
              } />
              <Route path="group-plays/:groupPlayId" element={
                <ProtectedRoute>
                  <ViewGroupPlayPage />
                </ProtectedRoute>
              } />
              <Route path="group-plays/:groupPlayId/instances/:instanceId" element={
                <ProtectedRoute>
                  <ViewGroupPlayInstancePage />
                </ProtectedRoute>
              } />
              <Route path="personal-trainings" element={
                <ProtectedRoute>
                  <PersonalTrainingsPage />
                </ProtectedRoute>
              } />
              <Route path="member-management" element={
                <ProtectedRoute>
                  <MemberManagementPage />
                </ProtectedRoute>
              } />
              <Route path="transactions" element={
                <ProtectedRoute>
                  <TransactionsPage />
                </ProtectedRoute>
              } />              
              <Route path="settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="financial-report" element={
                <ProtectedRoute>
                  <FinancialReport />
                </ProtectedRoute>
              } />
              <Route path="accounting-report" element={
                <ProtectedRoute>
                  <AccountingReport />
                </ProtectedRoute>
              } />
              <Route path="reports" element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              } />
              <Route path="management-report" element={
                <ProtectedRoute>
                  <ManagementReport />
                </ProtectedRoute>
              } />
              <Route path="coaching-report" element={
                <ProtectedRoute>
                  <CoachingReport />
                </ProtectedRoute>
              } />
              <Route path="arrears-report" element={
                <ProtectedRoute>
                  <ArrearsReport />
                </ProtectedRoute>
              } />
              <Route path="audit-logs" element={
                <ProtectedRoute>
                  <AuditLogsPage />
                </ProtectedRoute>
              } />
              <Route path="profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="select-member-profile" element={
                <ProtectedRoute>
                  <SelectMemberProfilePage />
                </ProtectedRoute>
              } />
              <Route path="member-profile" element={
                <ProtectedRoute>
                  <MemberProfilePage />
                </ProtectedRoute>
              } />
              <Route path="billing" element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              } />
              <Route path="documents" element={
                <ProtectedRoute>
                  <DocumentReviewPage />
                </ProtectedRoute>
              } />
              <Route path="clubs" element={
                <SuperAdminProtectedRoute>
                  <ClubsPage />
                </SuperAdminProtectedRoute>
              } />
              <Route path="transaction-processor" element={
                <SuperAdminProtectedRoute>
                  <TransactionProcessor />
                </SuperAdminProtectedRoute>
              } />
              <Route path="system-notifications" element={
                <SuperAdminProtectedRoute>
                  <SystemNotificationsPage />
                </SuperAdminProtectedRoute>
              } />
              <Route path="user-management" element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              } />
              <Route path="member-management" element={
                <ProtectedRoute>
                  <MemberManagementPage />
                </ProtectedRoute>
              } />
              <Route path="member-profiles/:id" element={
                <ProtectedRoute>
                  <MemberProfileDetailsPage />
                </ProtectedRoute>
              } />
              <Route path="member-profiles/:id/change-membership" element={
                <ProtectedRoute>
                  <ChangeMembershipPage />
                </ProtectedRoute>
              } />
              <Route path="users/:id" element={
                <ProtectedRoute>
                  <UserDetailsPage />
                </ProtectedRoute>
              } />              <Route path="membership-tiers" element={
                <ProtectedRoute>
                  <MembershipTierPage />
                </ProtectedRoute>
              } />
              <Route path="discounts" element={
                <ProtectedRoute>
                  <DiscountPage />
                </ProtectedRoute>
              } />
              <Route path="groups" element={
                <ProtectedRoute>
                  <GroupsPage />
                </ProtectedRoute>
              } />
              <Route path="one-time-fees" element={
                <ProtectedRoute>
                  <OneTimeFeesPage />
                </ProtectedRoute>
              } />
              <Route path="add-ons" element={
                <ProtectedRoute>
                  <AddOnManagementPage />
                </ProtectedRoute>
              } />
              <Route path="membership-applications" element={
                <ProtectedRoute>
                  <MembershipApplicationsPage />
                </ProtectedRoute>
              } />
              <Route path="membership-applications/:id" element={
                <ProtectedRoute>
                  <MembershipApplicationReviewPage />
                </ProtectedRoute>
              } />
              <Route path="create-member-account/:id" element={
                <ProtectedRoute>
                  <CreateMemberAccountPage />
                </ProtectedRoute>
              } />
              <Route path="events" element={<EventsPage />} />
              <Route path="event-management" element={
                <ProtectedRoute>
                  <EventManagementPage />
                </ProtectedRoute>
              } />
              <Route path="events/:eventId" element={
                <ProtectedRoute>
                  <EventDetailsPage />
                </ProtectedRoute>
              } />
              <Route path="guest-passes" element={
                <ProtectedRoute>
                  <GuestPassManagement />
                </ProtectedRoute>
              } />
              <Route path="kiosk" element={
                <ProtectedRoute>
                  <KioskView />
                </ProtectedRoute>
              } />
              <Route path="support" element={
                <ProtectedRoute>
                  <SupportPage />
                </ProtectedRoute>
              } />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
